'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';
import { playTutorialTalkSound } from '@/lib/sounds';

interface TypewriterOptions {
  speed?: number;
  enableSound?: boolean;
  /**
   * Override the per-tick sound. Defaults to the legacy
   * `playTutorialTalkSound` (squarewave chirp used by the pre-grad
   * mascot). The /tutorial route passes the polished
   * `playTutorialTypeSound` so the dialogue feels at home next to the
   * rest of the app's polished SFX. `null` disables the per-tick
   * sound entirely without forcing every caller to flip
   * `enableSound`.
   */
  tickSound?: (() => void) | null;
}

export function useTypewriter(text: string, options: TypewriterOptions = {}) {
  const { speed = 30, enableSound = false, tickSound } = options;
  const [displayed, setDisplayed] = useState('');
  const [isDone, setIsDone] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const indexRef = useRef(0);
  const soundCountRef = useRef(0);
  // Resolve the active per-tick sound at call time. `tickSound === null`
  // → silent; `undefined` → fall back to the legacy mascot chirp so
  // existing pre-grad call sites keep their voice.
  const playTick = tickSound === null
    ? null
    : tickSound ?? playTutorialTalkSound;

  useEffect(() => {
    setDisplayed('');
    setIsDone(false);
    indexRef.current = 0;
    soundCountRef.current = 0;

    intervalRef.current = setInterval(() => {
      indexRef.current++;
      const nextText = text.slice(0, indexRef.current);
      setDisplayed(nextText);

      if (enableSound && playTick) {
        const char = text[indexRef.current - 1];
        if (char && /[a-zA-Z0-9]/.test(char)) {
          soundCountRef.current++;
          if (soundCountRef.current % 2 === 0) {
            playTick();
          }
        }
      }

      if (indexRef.current >= text.length) {
        setIsDone(true);
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    }, speed);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [text, speed, enableSound, playTick]);

  const skipToEnd = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setDisplayed(text);
    setIsDone(true);
  }, [text]);

  return { displayed, isDone, skipToEnd };
}

interface TutorialMascotProps {
  isTalking: boolean;
  sizeClassName?: string;
}

export function TutorialMascot({
  isTalking,
  sizeClassName = 'w-24 h-24 sm:w-28 sm:h-28',
}: TutorialMascotProps) {
  return (
    <div className={cn('relative flex-shrink-0', sizeClassName)}>
      <img
        src="/takeoverman.png"
        alt="Takeover Man"
        className={cn(
          'w-full h-full object-contain drop-shadow-[0_0_12px_rgba(255,61,61,0.4)] transition-transform',
          isTalking && 'animate-mascot-talk'
        )}
        draggable={false}
      />
    </div>
  );
}

export function buildDiagonalOrder(): number[][] {
  const diagonals: number[][] = [];
  for (let d = 0; d <= 18; d++) {
    const group: number[] = [];
    for (let row = Math.max(0, d - 9); row <= Math.min(d, 9); row++) {
      const col = d - row;
      group.push(row * 10 + col);
    }
    diagonals.push(group);
  }
  return diagonals;
}
